import { makeAutoObservable, runInAction } from 'mobx'
import { SearchResourcesTask, type SearchHit } from '@/Ship/Tasks/SearchResourcesTask'

/**
 * Local store of `Ship/Components/UI/Search`.
 *
 * Owns the input value, fetched results, in-flight flag and dropdown
 * open state. Encapsulates the debounce timer + minimum-length gate (2 chars)
 * so the view stays declarative.
 *
 * Instantiated per Search mount via `useState(() => new SearchStore(800))`.
 */
export class SearchStore {
    searchValue = ''
    results: SearchHit[] = []
    isLoading = false
    isOpen = false
    /** Індекс результату, виділеного з клавіатури; -1 = нічого */
    activeIndex = -1
    /** Запит останнього виконаного пошуку (для стану «нічого не знайдено») */
    lastQuery = ''

    private debounceTimer: ReturnType<typeof setTimeout> | null = null
    private readonly debounceDelay: number

    constructor(debounceDelay: number = 800) {
        this.debounceDelay = debounceDelay
        makeAutoObservable<this, 'debounceTimer' | 'debounceDelay'>(this, {
            debounceTimer: false,
            debounceDelay: false,
        })
    }

    setOpen(open: boolean): void {
        this.isOpen = open
        if (!open) this.activeIndex = -1
    }

    /** Результат, виділений з клавіатури */
    get activeResult(): SearchHit | null {
        return this.results[this.activeIndex] ?? null
    }

    setActiveIndex(index: number): void {
        this.activeIndex = this.results.length ? Math.max(-1, Math.min(index, this.results.length - 1)) : -1
    }

    /** Стрілки ↑/↓: рух по результатах по колу */
    moveActive(delta: 1 | -1): void {
        const count = this.results.length
        if (!count) return
        this.activeIndex = (this.activeIndex + delta + count) % count
    }

    /** Результати, згруповані за ресурсом, у порядку появи */
    get groupedResults(): Array<{ resource: string; items: Array<{ hit: SearchHit; index: number }> }> {
        const groups = new Map<string, Array<{ hit: SearchHit; index: number }>>()
        this.results.forEach((hit, index) => {
            const list = groups.get(hit.resource) ?? []
            list.push({ hit, index })
            groups.set(hit.resource, list)
        })
        return [...groups.entries()].map(([resource, items]) => ({ resource, items }))
    }

    /**
     * Handle input change: track value, debounce search, clear results when
     * the query becomes too short (< 2 chars).
     */
    setSearchValue(value: string): void {
        this.searchValue = value

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
            this.debounceTimer = null
        }

        this.activeIndex = -1

        if (!value.trim() || value.trim().length < 2) {
            this.results = []
            this.isOpen = false
            this.isLoading = false
            this.lastQuery = ''
            return
        }

        this.debounceTimer = setTimeout(() => {
            this.performSearch(value)
        }, this.debounceDelay)
    }

    /**
     * Show dropdown when input gains focus and there is something to show.
     */
    onFocus(): void {
        const query = this.searchValue.trim()
        // Є що показати: результати або стан «нічого не знайдено» для цього ж запиту
        if (query.length >= 2 && (this.results.length > 0 || this.lastQuery === query)) {
            this.isOpen = true
        }
    }

    /**
     * Trigger an immediate search bypassing debounce (Enter / search-button click).
     */
    submitNow(): void {
        if (this.searchValue.trim()) {
            this.performSearch(this.searchValue)
        }
    }

    /**
     * Reset input + results without closing — used after a result click.
     */
    clear(): void {
        this.searchValue = ''
        this.results = []
        this.isOpen = false
        this.activeIndex = -1
        this.lastQuery = ''
    }

    /**
     * Cancel pending debounce timer — called from view's unmount effect.
     */
    cancelDebounce(): void {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
            this.debounceTimer = null
        }
    }

    /**
     * Execute the actual search request.
     * Guards against too-short queries (same 2-char gate as setSearchValue).
     */
    async performSearch(query: string): Promise<void> {
        if (!query.trim() || query.trim().length < 2) {
            runInAction(() => {
                this.results = []
                this.isOpen = false
                this.isLoading = false
            })
            return
        }
        this.isLoading = true
        try {
            const response = await SearchResourcesTask(query)
            runInAction(() => {
                // Порожня відповідь теж відкриває список: показуємо «нічого не знайдено»
                this.results = response
                this.activeIndex = -1
                this.lastQuery = query.trim()
                this.isOpen = true
            })
        } catch {
            runInAction(() => {
                this.results = []
                this.activeIndex = -1
                this.isOpen = false
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }
}
